import { defineMessages } from 'react-intl-next';

export const i18n = defineMessages({
	openAccessLabel: {
		id: 'share-and-restrict-dialog.general-access.level.open',
		defaultMessage: 'Open',
		description: 'Label for the open option for the general access levels',
	},
	restrictedAccessLabel: {
		id: 'share-and-restrict-dialog.general-access.level.restricted',
		defaultMessage: 'Restricted',
		description: 'Label for the restricted option for the general access levels',
	},
	restrictedByParentAccessLabel: {
		id: 'share-and-restrict-dialog.general-access.level.restricted-by-parent',
		defaultMessage: 'Restricted by a parent',
		description: 'Label for the restricted by parent option for the general access levels',
	},
	restrictedByParentTooltip: {
		id: 'share-and-restrict-dialog.general-access.level.restricted-by-parent-tooltip',
		defaultMessage: 'Can’t open to the space because access is limited by a parent item.',
		description:
			'Tooltip text which shows next to the "Open" general access level when the content is restricted by a parent',
	},
	restrictedByParentTooltipIconLabel: {
		id: 'share-and-restrict-dialog.general-access.level.restricted-by-parent-tooltip-icon-label',
		defaultMessage: 'Open disabled - More info',
		description:
			'Accessibility label for the tooltip icon which appears next to the "Open" general access level when it is disabled',
	},
	unlockIconLabel: {
		id: 'share-and-restrict-dialog.general-access.unlock-icon.label',
		defaultMessage: 'Open',
		description: 'Label for unlock icon in general access level',
	},
	lockIconLabel: {
		id: 'share-and-restrict-dialog.general-access.lock-icon.label',
		defaultMessage: 'Restricted',
		description: 'Label for lock icon in general access level',
	},
	dropdownIconLabel: {
		id: 'share-and-restrict-dialog.general-access.dropdown-icon.label',
		defaultMessage: 'Change general access level',
		description: 'Label for dropdown icon in general access level',
	},
});

export const standardSubtextI18n = defineMessages({
	anyoneInThisSpaceSubtext: {
		id: 'share-and-restrict-dialog.general-access.anyone-in-this-space',
		defaultMessage: 'Anyone in <a>this space</a>',
		description: 'Anyone in this space subtext for general access level',
	},
	anyoneOnTheParentSubtext: {
		id: 'share-and-restrict-dialog.general-access.anyone-on-parent',
		defaultMessage: 'Anyone on <parentLink>the parent</parentLink>',
		description: 'Anyone in this space subtext for general access level',
	},
	onlySpecificPeopleSubtext: {
		id: 'share-and-restrict-dialog.general-access.only-specific-people',
		defaultMessage: 'Only specific people can view or edit',
		description: 'Only specific people can view or edit subtext for general access level',
	},
	draftAnyoneInThisSpaceSubtext: {
		id: 'share-and-restrict-dialog.general-access.draft.anyone-in-this-space',
		defaultMessage: 'Anyone in <a>this space</a> (with the link)',
		description: 'Anyone in this space subtext for general access level',
	},
	draftAnyoneOnTheParentSubtext: {
		id: 'share-and-restrict-dialog.general-access.draft.anyone-on-parent',
		defaultMessage: 'Anyone on <parentLink>the parent</parentLink> (with the link)',
		description: 'Anyone in this space subtext for general access level',
	},
	draftOnlySpecificPeopleSubtext: {
		id: 'share-and-restrict-dialog.general-access.draft.only-specific-people',
		defaultMessage: 'Only specific people can view or edit (with the link) ',
		description: 'Only specific people can view or edit subtext for general access level',
	},
});

// All of the "long subtext" strings below are only used in the PublishModal.
// (As of 2024-08-14, it is the only place that uses the `showLongSubtext` prop.)
const longSubtextI18n = defineMessages({
	anyoneInThisSpaceCanViewAndEditSubtext: {
		id: 'share-and-restrict-dialog.general-access.anyone-in-this-space-can-view-and-edit',
		defaultMessage: 'Anyone in <a>this space</a> can view and edit',
		description: 'Anyone in this space can view and edit subtext for general access level',
	},
	anyoneInThisSpaceCanViewNotEditSubtext: {
		id: 'share-and-restrict-dialog.general-access.anyone-in-this-space-can-view-not-edit',
		defaultMessage: 'Anyone in <a>this space</a> can view, only some can edit',
		description: 'Anyone in this space can view but not edit subtext for general access level',
	},
	anyoneOnTheParentCanViewAndEditSubtext: {
		id: 'share-and-restrict-dialog.general-access.anyone-on-parent-can-view-and-edit',
		defaultMessage: 'Anyone on <parentLink>the parent</parentLink> can view and edit',
		description:
			'Anyone with access to the parent can view and edit subtext for general access level',
	},
	anyoneOnTheParentCanViewNotEditSubtext: {
		id: 'share-and-restrict-dialog.general-access.anyone-on-parent-can-view-not-edit',
		defaultMessage: 'Anyone on <parentLink>the parent</parentLink> can view, only some can edit',
		description:
			'Anyone with access to the parent can view but not edit subtext for general access level',
	},
	draftAnyoneInThisSpaceCanViewAndEditSubtext: {
		id: 'share-and-restrict-dialog.general-access.draft.anyone-in-this-space-can-view-and-edit',
		defaultMessage: 'Anyone in <a>this space</a> (with the link) can view and edit',
		description: 'Anyone in this space can view and edit subtext for general access level',
	},
	draftAnyoneInThisSpaceCanViewNotEditSubtext: {
		id: 'share-and-restrict-dialog.general-access.draft.anyone-in-this-space-can-view-not-edit',
		defaultMessage: 'Anyone in <a>this space</a> (with the link) can view, only some can edit',
		description: 'Anyone in this space can view but not edit subtext for general access level',
	},
	draftAnyoneOnTheParentCanViewAndEditSubtext: {
		id: 'share-and-restrict-dialog.general-access.draft.anyone-on-parent-can-view-and-edit',
		defaultMessage:
			'Anyone on <parentLink>the parent</parentLink> (with the link) can view and edit',
		description: 'Anyone in this space can view and edit subtext for general access level',
	},
	draftAnyoneOnTheParentCanViewNotEditSubtext: {
		id: 'share-and-restrict-dialog.general-access.draft.anyone-on-parent-can-view-not-edit',
		defaultMessage:
			'Anyone on <parentLink>the parent</parentLink> (with the link) can view, only some can edit',
		description: 'Anyone in this space can view but not edit subtext for general access level',
	},
});

export const longSubtextCanViewAndEditI18n = {
	anyoneInThisSpaceSubtext: longSubtextI18n.anyoneInThisSpaceCanViewAndEditSubtext,
	anyoneOnTheParentSubtext: longSubtextI18n.anyoneOnTheParentCanViewAndEditSubtext,
	onlySpecificPeopleSubtext: standardSubtextI18n.onlySpecificPeopleSubtext,
	draftAnyoneInThisSpaceSubtext: longSubtextI18n.draftAnyoneInThisSpaceCanViewAndEditSubtext,
	draftAnyoneOnTheParentSubtext: longSubtextI18n.draftAnyoneOnTheParentCanViewAndEditSubtext,
	draftOnlySpecificPeopleSubtext: standardSubtextI18n.draftOnlySpecificPeopleSubtext,
};
export const longSubtextCanViewNotEditI18n = {
	anyoneInThisSpaceSubtext: longSubtextI18n.anyoneInThisSpaceCanViewNotEditSubtext,
	anyoneOnTheParentSubtext: longSubtextI18n.anyoneOnTheParentCanViewNotEditSubtext,
	onlySpecificPeopleSubtext: standardSubtextI18n.onlySpecificPeopleSubtext,
	draftAnyoneInThisSpaceSubtext: longSubtextI18n.draftAnyoneInThisSpaceCanViewNotEditSubtext,
	draftAnyoneOnTheParentSubtext: longSubtextI18n.draftAnyoneOnTheParentCanViewNotEditSubtext,
	draftOnlySpecificPeopleSubtext: standardSubtextI18n.draftOnlySpecificPeopleSubtext,
};
